import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { of } from 'rxjs';
import { catchError, takeUntil, filter } from 'rxjs/operators';
import { NotifierService } from 'angular-notifier';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService implements OnDestroy {
  private readonly REQUEST_TIMEOUT_MS = 5000; // Request timeout
  private readonly LAST_VERSION_KEY = 'eagle-eye:last-known-version';
  
  private currentVersion: string = '';
  private lastKnownVersion: string = '';
  private versionCheckSubject = new Subject<{ newVersion: string; oldVersion: string }>();
  private isRefreshing: boolean = false;
  private destroy$ = new Subject<void>();
  private routerSubscription: Subscription | null = null;
  private isInitialized: boolean = false;
  private ongoingCheck: boolean = false;
  
  public versionUpdate$ = this.versionCheckSubject.asObservable();

  constructor(private http: HttpClient, private notifier: NotifierService, private router: Router) {}

  /**
   * Initialize version checking
   * @param currentVersion The current version to check against
   */
  public initVersionCheck(currentVersion: string): void {
    if (this.isInitialized) {
      return; // Already initialized
    }
    this.currentVersion = currentVersion;
    try {
      this.lastKnownVersion = sessionStorage.getItem(this.LAST_VERSION_KEY) || currentVersion;
    } catch (error) {
      console.warn('Version checking disabled: cannot access session storage safely.', error);
      return;
    }
    this.isInitialized = true;

    // Check version IMMEDIATELY on app load (no delay)
    this.performVersionCheck();

    // Subscribe to router navigation events and check version on each route change
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.performVersionCheck();
      });
  }

  /**
   * Cleanup resources - implement OnDestroy
   */
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }
    // Complete observables
    this.destroy$.next();
    this.destroy$.complete();
    this.versionCheckSubject.complete();
  }

  /**
   * Perform a version check - optimized for minimal performance impact
   */
  private performVersionCheck(): void {
    // Don't check while refreshing or if a check is already in progress
    if (this.isRefreshing || this.ongoingCheck) {
      return;
    }

    this.ongoingCheck = true;
    // Set timeout to cancel request if it takes too long
    // const timeoutId = setTimeout(() => {
    //   // Request will be silently cancelled after timeout
    // }, this.REQUEST_TIMEOUT_MS);

    this.checkVersion().subscribe({
      next: (response) => {
        this.ongoingCheck = false;
        // clearTimeout(timeoutId);
        const latestVersion = this.extractVersion(response);
        if (!latestVersion) {
          return; // Silent fail
        }

        // Only trigger if version actually changed
        if (latestVersion !== this.lastKnownVersion) {
          try {
            // Persist before refreshing so a page reload keeps the new comparison baseline.
            sessionStorage.setItem(this.LAST_VERSION_KEY, latestVersion);
          } catch (error) {
            console.warn('Automatic refresh skipped: could not save the latest version.', error);
            return;
          }
          this.lastKnownVersion = latestVersion;
          this.versionCheckSubject.next({
            newVersion: latestVersion,
            oldVersion: this.currentVersion
          });
          this.autoRefresh();
        }
      },
      error: () => {
        this.ongoingCheck = false;
        // clearTimeout(timeoutId);
        // Silent fail - version check is non-critical background task
      }
    });
  }

  /**
   * Check the latest version from the server
   * Uses cache-busting query parameter to ensure fresh response
   */
  private checkVersion() {
    const timestamp = new Date().getTime();
    const cacheUrl = `assets/version.json?t=${timestamp}`;
    
    return this.http.get<{ version: string }>(cacheUrl).pipe(
      catchError(() => {
        // Silent fallback to API
        return this.http
          .get<{ version: string }>(`/api/version?t=${timestamp}`)
          .pipe(catchError(() => of(null)));
      })
    );
  }

  /**
   * Extract version string from response object
   */
  private extractVersion(response: any): string {
    if (!response) return '';
    return (response.version || response) as string;
  }

  /**
   * Auto-refresh the page with a delay
   * Shows a notification before refreshing
   * Uses a cache-busting page URL; server cache headers still determine asset freshness.
   */
  private autoRefresh(delayMs: number = 3000): void {
    if (this.isRefreshing) {
      return; // Already refreshing
    }
    
    this.isRefreshing = true;
    
    // Show notification to user using application's notifier service
    // try {
    //   console.log('New version detected. Page will refresh to load new changes...');
    //   // this.notifier.notify('success', 'Version updated. Refreshing...');
    // } catch (e) {
    //   // Notifier may not be available, continue anyway
    // }
    
    // Reload page after delay with hard refresh (bypass cache)
    setTimeout(async () => {
      try {
        // Clear all service worker caches to bust lazy-loaded module chunks
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        }
      } catch (e) {
        console.log('Could not clear caches:', e);
      }
      
      // Make a no-cache fetch request to prime the browser with fresh content
      // This mimics Ctrl+Shift+R by sending Cache-Control: no-cache headers
      try {
        await fetch(window.location.href, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
      } catch (e) {
        console.log('No-cache fetch failed (expected), continuing with reload:', e);
      }
      
      // Hard refresh: Append timestamp to URL to force browser to fetch fresh resources
      const refreshUrl = window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'refresh=' + new Date().getTime();
      
      // Use native reload with force-refresh parameter
      // The additional URL parameter ensures browser doesn't use any cached response
      (window.location as any).href = refreshUrl;
      
    }, delayMs);
  }

  /**
   * Manually trigger version check (for testing or on-demand)
   */
  public checkNow(): void {
    // Reset the flag to allow immediate check
    this.ongoingCheck = false;
    this.performVersionCheck();
  }

  /**
   * Check version immediately on login
   * Call this from login service after successful authentication
   * This bypasses the ongoingCheck flag to ensure check happens immediately
   */
  public checkVersionOnLogin(): void {
    // Reset the flag to allow this check to proceed
    // this.ongoingCheck = false;
    // this.performVersionCheck();
  }
}
